import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../../config/supabase';
import './ChoiceBundleAuditPage.css';

/**
 * ChoiceBundleAuditPage - Admin tool for auditing choice bundles and their BOQ settings.
 * For choice bundles, typically only the selected child should generate BOQ items,
 * not the parent. This page helps review and fix that.
 * Also flags items with item_options that might need different BOQ per option
 * (candidates for conversion to choice bundles).
 * Access via /admin/choice-audit (no sidebar link needed).
 */
const ChoiceBundleAuditPage = () => {
  // Data state
  const [masterItems, setMasterItems] = useState([]);
  const [boqCategories, setBoqCategories] = useState({});
  const [costTypes, setCostTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // UI state
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedBundles, setExpandedBundles] = useState(new Set());

  // ─── Data fetching ───────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, catRes, costRes] = await Promise.all([
        supabase
          .from('master_items')
          .select(
            'id, item_name, is_bundle, bundle_type, bundle_items, include_in_boq, boq_description, boq_items, item_options, project_phase_id',
          )
          .order('item_name'),
        supabase.from('reference_boq_categories').select('id, name, category_number'),
        supabase.from('reference_cost_types').select('id, name'),
      ]);

      if (itemsRes.error) throw itemsRes.error;
      if (catRes.error) throw catRes.error;
      if (costRes.error) throw costRes.error;

      setMasterItems(itemsRes.data || []);

      const catMap = {};
      (catRes.data || []).forEach(c => {
        catMap[c.id] = `${c.category_number} - ${c.name}`;
      });
      setBoqCategories(catMap);

      const ctMap = {};
      (costRes.data || []).forEach(ct => {
        ctMap[ct.id] = ct.name;
      });
      setCostTypes(ctMap);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Computed ──────────────────────────────────────────────────

  // Index items by id
  const itemsById = useMemo(() => {
    const map = {};
    masterItems.forEach(item => {
      map[item.id] = item;
    });
    return map;
  }, [masterItems]);

  // Helper: check if item has BOQ entries
  function hasBoqEntries(item) {
    if (!item) return false;
    const oldEntries = item.boq_description?.entries || [];
    const newEntries = item.boq_items || [];
    return oldEntries.length > 0 || newEntries.length > 0;
  }

  // Helper: get BOQ entry details for display
  function getBoqEntries(item) {
    if (!item) return [];
    const entries = [];

    (item.boq_description?.entries || []).forEach((entry, idx) => {
      entries.push({
        system: 'old',
        index: idx,
        categoryName: boqCategories[entry.category_id] || '-',
        description: entry.description || '',
        measurementType: costTypes[entry.measurement_type] || '',
        quantityType: entry.quantity?.type || '',
      });
    });

    (item.boq_items || []).forEach((mboqId, idx) => {
      entries.push({
        system: 'new',
        index: idx,
        name: mboqId,
      });
    });

    return entries;
  }

  // Build choice bundle data
  const choiceBundles = useMemo(() => {
    return masterItems
      .filter(item => item.is_bundle && item.bundle_type === 'choice')
      .map(bundle => {
        const childIds = (bundle.bundle_items?.items || []).map(c => c.item_id);
        const children = childIds.map(id => itemsById[id]).filter(Boolean);

        const parentHasBoq = hasBoqEntries(bundle);
        const parentBoqOn = bundle.include_in_boq || false;
        const childrenWithBoq = children.filter(c => hasBoqEntries(c));
        const childrenWithBoqOn = children.filter(c => c.include_in_boq);

        // Issue: parent has include_in_boq=true on a choice bundle
        const parentBoqIssue = parentBoqOn && parentHasBoq;
        // Issue: parent has BOQ entries but children also have BOQ entries
        const doubleBoqIssue = parentHasBoq && childrenWithBoq.length > 0;
        // All good: only children have BOQ
        const isClean = !parentBoqIssue && !doubleBoqIssue;

        return {
          ...bundle,
          children,
          parentHasBoq,
          parentBoqOn,
          childrenWithBoq,
          childrenWithBoqOn,
          parentBoqIssue,
          doubleBoqIssue,
          isClean,
        };
      })
      .sort((a, b) => a.item_name.localeCompare(b.item_name, 'he'));
  }, [masterItems, itemsById]);

  // Build items-with-options that might need conversion to choice bundles
  const optionCandidates = useMemo(() => {
    return masterItems
      .filter(item => {
        if (item.is_bundle) return false;
        if (!item.item_options?.options || item.item_options.options.length === 0) return false;
        // Has BOQ and has multiple options → might need different BOQ per option
        return hasBoqEntries(item) && item.item_options.options.length > 1;
      })
      .sort((a, b) => a.item_name.localeCompare(b.item_name, 'he'));
  }, [masterItems]);

  // Stats
  const stats = useMemo(() => {
    const totalChoice = choiceBundles.length;
    const withIssues = choiceBundles.filter(b => !b.isClean).length;
    const clean = totalChoice - withIssues;
    const candidates = optionCandidates.length;
    return { totalChoice, withIssues, clean, candidates };
  }, [choiceBundles, optionCandidates]);

  // Filter
  const filteredBundles = useMemo(() => {
    let items = choiceBundles;

    switch (filter) {
      case 'issues':
        items = items.filter(b => !b.isClean);
        break;
      case 'clean':
        items = items.filter(b => b.isClean);
        break;
      case 'candidates':
        return []; // handled separately
      default:
        break;
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      items = items.filter(
        b =>
          b.item_name.toLowerCase().includes(term) ||
          b.children.some(c => c.item_name.toLowerCase().includes(term)),
      );
    }

    return items;
  }, [choiceBundles, filter, searchTerm]);

  const filteredCandidates = useMemo(() => {
    if (filter !== 'candidates') return [];
    let items = optionCandidates;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      items = items.filter(i => i.item_name.toLowerCase().includes(term));
    }
    return items;
  }, [optionCandidates, filter, searchTerm]);

  // ─── Actions ───────────────────────────────────────────────────

  // Toggle include_in_boq
  const handleToggleBoq = async (itemId, currentValue) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('master_items')
        .update({ include_in_boq: !currentValue })
        .eq('id', itemId);
      if (error) throw error;

      setMasterItems(prev =>
        prev.map(item => (item.id === itemId ? { ...item, include_in_boq: !currentValue } : item)),
      );
    } catch (err) {
      console.error('Error toggling BOQ:', err);
      alert('שגיאה בעדכון: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Toggle expand
  const toggleExpand = id => {
    setExpandedBundles(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const expandAll = () => {
    const ids =
      filter === 'candidates' ? optionCandidates.map(i => i.id) : filteredBundles.map(b => b.id);
    setExpandedBundles(new Set(ids));
  };

  const collapseAll = () => {
    setExpandedBundles(new Set());
  };

  // ─── Render: BOQ entries ───────────────────────────────────────

  const renderBoqEntries = item => {
    const entries = getBoqEntries(item);
    if (entries.length === 0) {
      return <div className="ca-no-boq">אין שורות BOQ</div>;
    }

    return (
      <div className="ca-boq-entries">
        {entries.map((entry, idx) => (
          <div key={idx} className="ca-boq-entry">
            {entry.system === 'old' ? (
              <>
                <div className="ca-boq-entry-cat">{entry.categoryName}</div>
                <div className="ca-boq-entry-desc">{entry.description || '(ריק)'}</div>
                {entry.measurementType && (
                  <div className="ca-boq-entry-meta">יחידה: {entry.measurementType}</div>
                )}
              </>
            ) : (
              <div className="ca-boq-entry-desc">master_boq_item: {entry.name}</div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // ─── Render: choice bundle card ────────────────────────────────

  const renderChoiceBundleCard = bundle => {
    const isExpanded = expandedBundles.has(bundle.id);

    return (
      <div key={bundle.id} className={`ca-bundle-card ${!bundle.isClean ? 'ca-has-issue' : ''}`}>
        <div className="ca-bundle-header" onClick={() => toggleExpand(bundle.id)}>
          <span className={`ca-expand-icon ${isExpanded ? 'expanded' : ''}`}>▶</span>
          <span className="ca-bundle-title">{bundle.item_name}</span>
          <div className="ca-bundle-badges">
            <span
              className={`ca-badge ${bundle.parentBoqOn ? 'ca-badge-boq-on' : 'ca-badge-boq-off'}`}
            >
              הורה BOQ: {bundle.parentBoqOn ? '✓' : '✗'}
            </span>
            {bundle.childrenWithBoqOn.length > 0 && (
              <span className="ca-badge ca-badge-boq-on">
                ילדים עם BOQ: {bundle.childrenWithBoqOn.length}
              </span>
            )}
            {!bundle.isClean ? (
              <span className="ca-badge ca-badge-warning">⚠ לבדיקה</span>
            ) : (
              <span className="ca-badge ca-badge-ok">✓ תקין</span>
            )}
            <span className="ca-badge ca-badge-children">{bundle.children.length} אפשרויות</span>
          </div>
        </div>

        {isExpanded && (
          <div className="ca-bundle-body">
            {/* Issue banners */}
            {bundle.parentBoqIssue && (
              <div className="ca-info-banner warning">
                ⚠️ חבילת בחירה עם &quot;הצג בכתב כמויות&quot; על ההורה — בד&quot;כ רק הילד הנבחר
                צריך להופיע
              </div>
            )}
            {bundle.doubleBoqIssue && (
              <div className="ca-info-banner warning">
                ⚠️ גם להורה וגם לילדים יש שורות BOQ — ייתכנו כפילויות
              </div>
            )}
            {bundle.isClean && (
              <div className="ca-info-banner info">✓ תקין — BOQ מוגדר רק על הילדים</div>
            )}

            {/* Parent BOQ section */}
            <div className="ca-parent-section">
              <div className="ca-section-label">הורה (חבילה)</div>
              <div className="ca-parent-boq-toggle">
                <label>
                  <input
                    type="checkbox"
                    checked={bundle.include_in_boq || false}
                    onChange={() => handleToggleBoq(bundle.id, bundle.include_in_boq)}
                    disabled={saving}
                  />
                  הצג בכתב כמויות
                </label>
                <span style={{ color: '#9ca3af', fontSize: 12 }}>
                  ({getBoqEntries(bundle).length} שורות BOQ)
                </span>
              </div>
              {renderBoqEntries(bundle)}
            </div>

            {/* Children table */}
            <div className="ca-children-section">
              <table className="ca-children-table">
                <thead>
                  <tr>
                    <th>אפשרות (ילד)</th>
                    <th style={{ width: '120px' }}>הצג בכתב כמויות</th>
                    <th>שורות BOQ</th>
                  </tr>
                </thead>
                <tbody>
                  {bundle.children.map(child => (
                    <tr key={child.id}>
                      <td>
                        <span className="ca-child-name">{child.item_name}</span>
                      </td>
                      <td>
                        <div className="ca-child-boq-toggle">
                          <label>
                            <input
                              type="checkbox"
                              checked={child.include_in_boq || false}
                              onChange={() => handleToggleBoq(child.id, child.include_in_boq)}
                              disabled={saving}
                            />
                            {child.include_in_boq ? '✓' : '✗'}
                          </label>
                        </div>
                      </td>
                      <td>{renderBoqEntries(child)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── Render: option candidate card ─────────────────────────────

  const renderCandidateCard = item => {
    const isExpanded = expandedBundles.has(item.id);
    const optionCount = item.item_options?.options?.length || 0;

    return (
      <div key={item.id} className="ca-bundle-card ca-has-issue">
        <div className="ca-bundle-header" onClick={() => toggleExpand(item.id)}>
          <span className={`ca-expand-icon ${isExpanded ? 'expanded' : ''}`}>▶</span>
          <span className="ca-bundle-title">{item.item_name}</span>
          <div className="ca-bundle-badges">
            <span className="ca-badge ca-badge-warning">{optionCount} אפשרויות + BOQ</span>
            <span className="ca-badge ca-badge-boq-on">{getBoqEntries(item).length} שורות BOQ</span>
          </div>
        </div>

        {isExpanded && (
          <div className="ca-bundle-body">
            <div className="ca-info-banner warning">
              💡 לפריט זה יש {optionCount} אפשרויות ושורות BOQ. אם אפשרויות שונות דורשות BOQ שונה,
              שקול להמיר לחבילת בחירה (choice bundle) עם ילדים נפרדים.
            </div>

            {/* Current BOQ */}
            <div className="ca-parent-section">
              <div className="ca-section-label">שורות BOQ נוכחיות</div>
              {renderBoqEntries(item)}
            </div>

            {/* Options list */}
            <div className="ca-parent-section" style={{ background: '#fefce8' }}>
              <div className="ca-section-label">אפשרויות (item_options)</div>
              <table className="ca-children-table">
                <thead>
                  <tr>
                    <th>שם אפשרות</th>
                    <th>קבוצה</th>
                    <th>עלות ברירת מחדל</th>
                    <th>תלויות</th>
                  </tr>
                </thead>
                <tbody>
                  {(item.item_options?.options || []).map((opt, idx) => (
                    <tr key={idx}>
                      <td style={{ fontWeight: 500 }}>{opt.name || '-'}</td>
                      <td style={{ color: '#6b7280', fontSize: 12 }}>{opt.group || '-'}</td>
                      <td style={{ fontSize: 12 }}>
                        {opt.default_cost != null
                          ? `₪${Number(opt.default_cost).toLocaleString()}`
                          : '-'}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {opt.dependencies?.length > 0 ? `${opt.dependencies.length} תלויות` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── Main render ───────────────────────────────────────────────

  if (loading) {
    return (
      <div className="choice-audit-page">
        <div className="choice-audit-loading">טוען נתונים...</div>
      </div>
    );
  }

  return (
    <div className="choice-audit-page">
      {/* Header */}
      <div className="choice-audit-header">
        <h1>ביקורת חבילות בחירה ו-BOQ</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {saving && <span className="ca-saving-indicator">שומר...</span>}
          <input
            type="text"
            className="choice-audit-search"
            placeholder="חיפוש..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Stats */}
      <div className="choice-audit-stats">
        <div className="choice-audit-stat">
          חבילות בחירה: <strong>{stats.totalChoice}</strong>
        </div>
        <div className={`choice-audit-stat ${stats.withIssues > 0 ? 'ca-stat-warning' : ''}`}>
          לבדיקה: <strong>{stats.withIssues}</strong>
        </div>
        <div className="choice-audit-stat">
          תקינות: <strong>{stats.clean}</strong>
        </div>
        <div className={`choice-audit-stat ${stats.candidates > 0 ? 'ca-stat-warning' : ''}`}>
          מועמדים להמרה: <strong>{stats.candidates}</strong>
        </div>
      </div>

      {/* Filters */}
      <div className="choice-audit-filters">
        {[
          { key: 'all', label: `כל חבילות הבחירה (${stats.totalChoice})` },
          { key: 'issues', label: `⚠ לבדיקה (${stats.withIssues})` },
          { key: 'clean', label: `✓ תקינות (${stats.clean})` },
          { key: 'candidates', label: `💡 מועמדים להמרה (${stats.candidates})` },
        ].map(f => (
          <button
            key={f.key}
            className={`ca-filter-btn ${filter === f.key ? 'active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
        <div style={{ marginRight: 'auto', display: 'flex', gap: 6 }}>
          <button className="ca-filter-btn" onClick={expandAll}>
            פתח הכל
          </button>
          <button className="ca-filter-btn" onClick={collapseAll}>
            סגור הכל
          </button>
        </div>
      </div>

      {/* Content */}
      {filter === 'candidates' ? (
        // Show option candidates
        filteredCandidates.length === 0 ? (
          <div className="choice-audit-empty">אין מועמדים להמרה</div>
        ) : (
          filteredCandidates.map(item => renderCandidateCard(item))
        )
      ) : // Show choice bundles
      filteredBundles.length === 0 ? (
        <div className="choice-audit-empty">אין חבילות בחירה להצגה</div>
      ) : (
        filteredBundles.map(bundle => renderChoiceBundleCard(bundle))
      )}
    </div>
  );
};

export default ChoiceBundleAuditPage;
